import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, ArrowRight, Check } from "lucide-react";
import { useProducts } from "../context/ProductsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOTH_IMAGE = "/booth-hero.jpg";

export default function Markets() {
  const { products } = useProducts();
  const [markets, setMarkets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preorderItems, setPreorderItems] = useState({});
  const [form, setForm] = useState({ name: "", email: "" });
  const [placed, setPlaced] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/markets`)
      .then((r) => {
        setMarkets(r.data.markets || []);
        if (r.data.markets?.length) setSelected(r.data.markets[0]);
      })
      .catch(() => {});
  }, []);

  const inc = (id) =>
    setPreorderItems((s) => ({ ...s, [id]: (s[id] || 0) + 1 }));
  const dec = (id) =>
    setPreorderItems((s) => {
      const next = { ...s, [id]: Math.max(0, (s[id] || 0) - 1) };
      if (next[id] === 0) delete next[id];
      return next;
    });

  const orderLines = useMemo(
    () =>
      Object.entries(preorderItems)
        .map(([id, qty]) => {
          const p = products.find((x) => x.id === id);
          return p ? { ...p, qty } : null;
        })
        .filter(Boolean),
    [preorderItems, products]
  );

  const total = orderLines.reduce((s, l) => s + l.price * l.qty, 0);

  const submitPreorder = async (e) => {
    e.preventDefault();
    if (!selected || orderLines.length === 0) {
      toast.error("Add at least one bottle to your pre-order.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/preorder`, {
        name: form.name,
        email: form.email,
        market: selected.name,
        date: selected.next_date,
        items: orderLines.map((l) => ({
          id: l.id,
          name: l.name,
          price: l.price,
          quantity: l.qty,
          image: l.image,
        })),
      });
      setPlaced({ id: data.id, market: selected.name, date: selected.next_date });
      setPreorderItems({});
      setForm({ name: "", email: "" });
    } catch (err) {
      toast.error("Could not place pre-order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div data-testid="markets-page" className="bg-[color:var(--sr-bg)] pt-28 md:pt-32">
      <section className="sr-container">
        <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Farmers markets
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-serif text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] tracking-tight text-[color:var(--sr-primary)] max-w-5xl"
        >
          Under the linen canopy,
          <br />
          <em className="italic font-light text-[color:var(--sr-accent)]">
            saturdays.
          </em>
        </motion.h1>
      </section>

      <section className="sr-container mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1 }}
          className="col-span-12 lg:col-span-7"
        >
          <div className="relative aspect-[5/4] w-full overflow-hidden bg-[color:var(--sr-secondary)]">
            <img src={BOOTH_IMAGE} alt="Smiling Roots booth" className="w-full h-full object-cover" />
          </div>
        </motion.div>
        <div className="col-span-12 lg:col-span-5 lg:pl-8 flex flex-col justify-center">
          <p className="text-[color:var(--sr-text)]/75 leading-relaxed max-w-md">
            Meet us in person. Sample, ask, and take a few bottles home. Our
            small team is at every market — we love a conversation about
            slow craft.
          </p>
        </div>
      </section>

      {/* Illustrated map + list */}
      <section className="sr-container mt-24 md:mt-40 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-7">
          <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6">Central Florida</p>
          <div className="relative aspect-[16/10] bg-[color:var(--sr-secondary)] overflow-hidden">
            {/* Stylized Florida silhouette */}
            <svg
              viewBox="0 0 200 125"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              <path
                d="M 12 26 L 30 22 L 60 21 L 90 22 L 108 22 C 118 22, 126 24, 132 28 C 138 32, 142 38, 143 46 C 145 55, 145 66, 141 78 C 137 92, 132 105, 126 116 C 122 122, 116 124, 111 121 C 105 117, 102 108, 102 98 C 103 88, 106 78, 108 68 C 109 60, 106 54, 100 50 C 92 45, 80 44, 66 44 C 50 44, 34 42, 22 38 C 16 36, 12 32, 12 26 Z"
                fill="#214B38"
                fillOpacity="0.10"
              />
              {/* Faint coastline highlight */}
              <path
                d="M 12 26 L 30 22 L 60 21 L 90 22 L 108 22 C 118 22, 126 24, 132 28 C 138 32, 142 38, 143 46 C 145 55, 145 66, 141 78 C 137 92, 132 105, 126 116 C 122 122, 116 124, 111 121 C 105 117, 102 108, 102 98 C 103 88, 106 78, 108 68 C 109 60, 106 54, 100 50 C 92 45, 80 44, 66 44 C 50 44, 34 42, 22 38 C 16 36, 12 32, 12 26 Z"
                fill="none"
                stroke="#214B38"
                strokeWidth="0.35"
                strokeOpacity="0.25"
              />
            </svg>
            {markets.map((m) => {
              const active = selected?.id === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  data-testid={`map-pin-${m.id}`}
                  className="absolute group"
                  style={{ left: `${m.coords.x}%`, top: `${m.coords.y}%`, transform: "translate(-50%, -50%)" }}
                  aria-label={m.name}
                >
                  <span className="relative flex flex-col items-center">
                    {active ? (
                      <span className="text-[color:var(--sr-primary)]">
                        <MapPin size={26} strokeWidth={1.25} fill="#B89B5E" />
                      </span>
                    ) : (
                      <span
                        className="block w-2.5 h-2.5 rounded-full bg-[color:var(--sr-primary)] transition-transform group-hover:scale-150"
                        style={{ boxShadow: "0 0 0 4px rgba(33,75,56,0.10)" }}
                      />
                    )}
                    {active && (
                      <span className="mt-1 label-eyebrow text-[10px] text-[color:var(--sr-primary)] bg-[color:var(--sr-bg)]/95 px-2 py-1 whitespace-nowrap absolute top-full">
                        {m.city.split(",")[0]}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
            {/* Corner label */}
            <div className="absolute bottom-4 right-4 text-right">
              <p className="label-eyebrow text-[color:var(--sr-primary)]/70">Florida</p>
              <p className="font-serif text-xl text-[color:var(--sr-primary)]/85 leading-none mt-1">
                <em className="italic">Central</em>
              </p>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6">Schedule</p>
          <ul className="border-t hairline">
            {markets.map((m) => (
              <li
                key={m.id}
                onClick={() => setSelected(m)}
                data-testid={`market-list-${m.id}`}
                className={`border-b hairline py-5 cursor-pointer transition-colors ${
                  selected?.id === m.id ? "bg-[color:var(--sr-secondary)]/60" : "hover:bg-[color:var(--sr-secondary)]/30"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-2xl text-[color:var(--sr-primary)] leading-tight">
                      {m.name}
                    </p>
                    <p className="text-xs text-[color:var(--sr-muted)] mt-1">{m.city}</p>
                  </div>
                  <div className="text-right">
                    <p className="label-eyebrow text-[color:var(--sr-primary)]">{m.day}</p>
                    <p className="text-xs text-[color:var(--sr-muted)] tabular-nums mt-1">{m.hours}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Google Maps embed */}
      {selected && (
        <section className="sr-container mt-16 md:mt-24" data-testid="markets-map-embed">
          <div className="grid grid-cols-12 gap-6 md:gap-12 items-end mb-8">
            <div className="col-span-12 md:col-span-7">
              <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3 flex items-center gap-3">
                <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> On the map
              </p>
              <h3 className="font-serif text-3xl md:text-5xl text-[color:var(--sr-primary)] tracking-tight leading-tight">
                {selected.name}
                <br />
                <em className="italic font-light text-[color:var(--sr-accent)] text-2xl md:text-3xl">
                  {selected.city}
                </em>
              </h3>
            </div>
            <p className="col-span-12 md:col-span-5 text-sm text-[color:var(--sr-muted)] leading-relaxed">
              Search by name or tap the map to open turn-by-turn directions in Google Maps.
            </p>
          </div>
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-[color:var(--sr-secondary)] hairline border">
            <iframe
              key={selected.id}
              title={`Map — ${selected.name}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                `${selected.name}, ${selected.city}`
              )}&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
              data-testid="google-maps-iframe"
            />
          </div>
        </section>
      )}

      {/* Preorder */}
      <section className="mt-24 md:mt-40 bg-[color:var(--sr-secondary)] grain py-24 md:py-32">
        <div className="sr-container grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-4">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Pre-order pickup
            </p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1] tracking-tight text-[color:var(--sr-primary)]">
              Reserve your bottles.
            </h2>
            <p className="mt-6 text-[color:var(--sr-text)]/75 leading-relaxed max-w-sm">
              Choose a market and the bottles you want. We hold them under
              your name for pickup at the booth.
            </p>
            {selected && (
              <div className="mt-8 border-t hairline pt-6 space-y-2">
                <p className="label-eyebrow text-[color:var(--sr-muted)]">Selected market</p>
                <p className="font-serif text-3xl text-[color:var(--sr-primary)]">{selected.name}</p>
                <p className="text-sm text-[color:var(--sr-muted)]">
                  {selected.next_date} · {selected.hours}
                </p>
              </div>
            )}
          </div>

          <div className="col-span-12 md:col-span-8">
            {placed ? (
              <div className="border hairline p-8 md:p-12 bg-[color:var(--sr-bg)]" data-testid="preorder-confirmation">
                <div className="w-12 h-12 rounded-full border hairline flex items-center justify-center text-[color:var(--sr-primary)]">
                  <Check size={20} strokeWidth={1.25} />
                </div>
                <p className="font-serif text-4xl text-[color:var(--sr-primary)] mt-6 leading-tight">
                  Reserved.
                </p>
                <p className="mt-3 text-[color:var(--sr-muted)] max-w-md">
                  Look for the linen canopy at <span className="text-[color:var(--sr-primary)]">{placed.market}</span>
                  {" "}on {placed.date}. We will be waiting. Ref{" "}
                  <span data-testid="preorder-id" className="text-[color:var(--sr-primary)]">{placed.id.slice(0, 8)}</span>.
                </p>
                <button
                  onClick={() => setPlaced(null)}
                  className="mt-8 label-eyebrow underline underline-offset-8 decoration-[color:var(--sr-accent)]"
                  data-testid="preorder-reset"
                >
                  Reserve another
                </button>
              </div>
            ) : (
              <form onSubmit={submitPreorder} className="space-y-8" data-testid="preorder-form">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  {products.map((p) => {
                    const qty = preorderItems[p.id] || 0;
                    return (
                      <li key={p.id} className="border hairline p-4 flex items-center gap-4 bg-[color:var(--sr-bg)]">
                        <div className="w-14 h-16 overflow-hidden bg-[color:var(--sr-secondary)] shrink-0">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <p className="font-serif text-lg text-[color:var(--sr-primary)] leading-tight">{p.name}</p>
                          <p className="text-xs text-[color:var(--sr-muted)] tabular-nums mt-0.5">${p.price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => dec(p.id)}
                            data-testid={`preorder-dec-${p.id}`}
                            className="p-1 border hairline text-[color:var(--sr-primary)]"
                            aria-label="Decrease"
                          >
                            −
                          </button>
                          <span data-testid={`preorder-qty-${p.id}`} className="w-5 text-center tabular-nums text-sm">{qty}</span>
                          <button
                            type="button"
                            onClick={() => inc(p.id)}
                            data-testid={`preorder-inc-${p.id}`}
                            className="p-1 border hairline text-[color:var(--sr-primary)]"
                            aria-label="Increase"
                          >
                            +
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="grid grid-cols-2 gap-5">
                  <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pre-name" required />
                  <Field type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="pre-email" required />
                </div>

                <div className="flex items-center justify-between border-t hairline pt-6">
                  <span className="label-eyebrow text-[color:var(--sr-muted)]">Reserved total</span>
                  <span className="font-serif text-3xl text-[color:var(--sr-primary)] tabular-nums" data-testid="preorder-total">
                    ${total.toFixed(2)}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={placing}
                  data-testid="preorder-submit"
                  className="group inline-flex items-center justify-between w-full px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
                >
                  <span>{placing ? "Reserving" : "Reserve for pickup"}</span>
                  <ArrowRight size={16} strokeWidth={1.25} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-[color:var(--sr-muted)]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required={required}
        className="w-full bg-transparent border-b hairline py-3 text-[color:var(--sr-primary)] outline-none focus:border-[color:var(--sr-primary)]"
      />
    </label>
  );
}
